import {Component, Inject, OnDestroy, Optional} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogContent} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {TranslatableComponent} from "../../../../../core/models/TranslatableComponent";
import {MephistoConnectionService} from "../../../../../core/services/services/mephisto-connection.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {AlertEnum} from "../../../../../core/models/Enums/AlertEnum";
import {MessageService} from "../../../../../core/services/services/message.service";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {MatFormField, MatLabel, MatError} from "@angular/material/form-field";
import {MatSelect} from "@angular/material/select";
import {MatOption} from "@angular/material/autocomplete";
import {FileDragDropComponent} from "../../../../../shared/file-input/components/file-drag-drop/file-drag-drop.component";
import {ReactiveFormsModule, FormsModule} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

@Component({
	selector: "app-mephisto-upload-dialog",
	templateUrl: "./mephisto-upload-dialog.component.html",
	styleUrls: ["./mephisto-upload-dialog.component.scss"],
	imports: [
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		FileDragDropComponent,
		ReactiveFormsModule,
		FormsModule,
		MatError,
		MatInput,
		CdkTextareaAutosize,
		MatProgressSpinner,
	],
})
export class MephistoUploadDialogComponent extends TranslatableComponent implements OnDestroy {
	uploadType: string;
	rawText: string;
	selectedFile: File;
	isInvalidFile: boolean = false;
	isLoading: boolean = false;
	hasMephistoAccess: boolean = false;
	showDelayMessage: boolean = false;
	errorMessageShown: string;
	private delayMessageTimeout: any;
	private destroy$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<MephistoUploadDialogComponent>,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any,
		public translate: TranslateService,
		private mephistoConnectionService: MephistoConnectionService,
		private messageService: MessageService,
	) {
		super(translate, "MephistoUploadDialogComponent");
		this.dialogRef.addPanelClass("mep-dialog");
		this.hasMephistoAccess = this.checkMephistoAccess();
	}

	checkMephistoAccess(): boolean {
		return true;
	}

	onFileChosen(file: File) {
		if (file && file.type === "application/pdf") {
			this.selectedFile = file;
			this.isInvalidFile = false;
		} else {
			this.selectedFile = null;
			this.isInvalidFile = true;
		}
	}

	async uploadData() {
		if (this.isUploadDisabled()) {
			return;
		}

		this.errorMessageShown = "";
		this.showDelayMessage = false;
		this.delayMessageTimeout = setTimeout(() => {
			this.showDelayMessage = true;
		}, 5000);

		if (this.uploadType === "pdf" && this.selectedFile) {
			this.uploadPdfToServer();
		} else {
			this.uploadRawTextToServer();
		}
	}

	uploadPdfToServer() {
		this.isLoading = true;
		const formData = new FormData();
		formData.append("file", this.selectedFile);

		this.mephistoConnectionService
			.uploadPdfAndPollStaffingRequest(formData)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (resourceRequest) => {
					if (resourceRequest.error) {
						this.errorMessageShown = this.translate.instant(resourceRequest.error.key);
					} else {
						this.dialogRef.close(resourceRequest);
					}
				},
				error: (error) => {
					this.messageService.add(
						this.translate.instant("mep.components.mephisto-upload-dialog.upload-error"),
						AlertEnum.DANGER,
					);
					console.error("Upload error:", error);
					this.isLoading = false;
				},
				complete: () => {
					clearTimeout(this.delayMessageTimeout);
				},
			});
	}

	uploadRawTextToServer() {
		this.isLoading = true;
		this.mephistoConnectionService
			.uploadRawTextAndPollStaffingRequest(this.rawText)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (resourceRequest) => {
					this.isLoading = false;
					if (resourceRequest.error) {
						this.errorMessageShown = this.translate.instant(resourceRequest.error.key);
					} else {
						this.dialogRef.close(resourceRequest);
					}
				},
				error: (error) => {
					this.messageService.add(
						this.translate.instant("mep.components.mephisto-upload-dialog.upload-error"),
						AlertEnum.DANGER,
					);
					console.error("Upload error:", error);
					this.isLoading = false;
				},
				complete: () => {
					clearTimeout(this.delayMessageTimeout);
				},
			});
	}

	isUploadDisabled(): boolean {
		if (this.uploadType === "pdf") {
			return !this.selectedFile;
		}
		if (this.uploadType === "text") {
			return !this.rawText;
		}
		return true;
	}

	removeErrorMessage() {
		this.errorMessageShown = "";
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	closeDialog() {
		this.destroy$.next();
		this.dialogRef.close(null);
	}
}
